import AppContext from 'App/AppContext';
import { IPStack } from 'baseComponents/Stack/IPStack';
import DashboardCollection from 'components/Dashboard/DashboardCollection';
import InfoMessage from 'components/Notification/InfoMessage';
import SceneBar from 'components/SceneBar/SceneBar';
import { globalKB_PartnerCenter } from 'globalConstants';
import {
  globalPaddingScene,
  globalStackStylesHeight100,
  globalStackTokensGapLarge,
  globalStackTokensGapSmall,
} from 'globalStyles';
import AdminTenant from 'models/adminTenant';
import { DashboardTypes } from 'models/dashboard';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';
import { apiGetPartnerAccessibleCustomerIds, apiGetTenantsForPartner } from 'services/Api/partnerService';
import { apiRequest } from 'services/Auth/authConfig';

interface IPartnerDashboardProps extends RouteComponentProps {}

export const PartnerDashboardContent = () => {
  const { t } = useTranslation(['partnerAdmin']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [partnerCustomers, setPartnerCustomers] = useState<AdminTenant[] | undefined>(undefined);
  const [hiddenCustomerCount, setHiddenCustomerCount] = useState<number>(0);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      appContext.showContentLoader();

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const [newPartnerCustomers, accessibleCustomerIds] = await Promise.all([
        apiGetTenantsForPartner(accessToken, appContext.globalDataCache),
        apiGetPartnerAccessibleCustomerIds(accessToken),
      ]);

      //The widget endpoints silently leave out every customer where this person has no licensed account, so a
      //dashboard "per customer" can show one bar for a partner with six customers. Count them here and say so
      //above the widgets; the widgets themselves stay as they are.
      const accessible = new Set(accessibleCustomerIds.map((id) => id.toLowerCase()));
      const hiddenCount = newPartnerCustomers.filter((c) => !accessible.has(c.tenantId.toLowerCase())).length;

      setPartnerCustomers(newPartnerCustomers);
      setHiddenCustomerCount(hiddenCount);
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
      setIsLoading(false);
    }
  };

  if (!partnerCustomers) {
    return null;
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      {hiddenCustomerCount > 0 && (
        <IPStack.Item>
          <InfoMessage
            message={
              hiddenCustomerCount === 1
                ? t('partnerAdmin:Dashboard.HiddenCustomers_one', { count: 1 })
                : t('partnerAdmin:Dashboard.HiddenCustomers_other', { count: hiddenCustomerCount })
            }
          />
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        <DashboardCollection
          dashboardType={DashboardTypes.partner}
          partnerCustomers={partnerCustomers}
          onRefreshSharedData={loadData}
        />
      </IPStack.Item>
    </IPStack>
  );
};

const PartnerDashboard = (props: IPartnerDashboardProps) => {
  const { t } = useTranslation(['partnerAdmin', 'dashboard', 'widgets']);

  //
  // Main render
  //
  return (
    <IPStack horizontal styles={globalStackStylesHeight100}>
      <IPStack.Item grow>
        <IPStack verticalFill styles={globalStackStylesHeight100} tokens={globalStackTokensGapLarge}>
          <IPStack.Item>
            <SceneBar
              title={t('partnerAdmin:DashboardTitle')}
              subtitle={t('partnerAdmin:DashboardSubtitle')}
              learnMoreLink={globalKB_PartnerCenter}
              image="dashboard.png"
              pivotItems={undefined}
              selectedPivotKey={undefined}
            />
          </IPStack.Item>
          <IPStack.Item grow styles={{ root: { paddingLeft: globalPaddingScene } }}>
            <PartnerDashboardContent />
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};

export default PartnerDashboard;
